/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import invert from 'lodash.invert';
import isNil from 'lodash.isnil';
import {translate} from '../locale/locale';
import {entityId} from '../services/ConnectUtils';
import {
  Asset,
  AssetState,
  AssetType,
  BaseAsset,
  CVSAssetType,
  EntityStatus,
  HVSRecord,
  ToolStatus
} from '../types/entities';
import {isTrue} from '../utils/BooleanUtils';
import {safeJSONParse} from '../utils/JSONUtils';
import {isEmpty, titleCase} from '../utils/StringUtil';

interface AddAsset {
  items: any[],
  assets?: Array<CVSAssetType>,
  selectedAsset: CVSAssetType,
  include?: boolean
};

// Defines the speed threshold value
export const speedThreshold = 1;

// Defines a utility to return the label of asset state based on assetState localization.
export const getAssetStateLabel = (value: string) => {
  return !isEmpty(value) ? translate(`common:assetState.${value}`) : value;
};

/**
 * Defines a utility to return the key of an element of an array of objects by the value.
 *
 *  ```typescript
 *   import {getItemKey} from '@ion/core/src/entities';
 *
 *   const data = {
 *     towed : 'Towed'
 *     ...
 *   };
 *
 *   console.log(getItemKey(data, 'Towed')) // towed
 *
 *  ```
 */
export const getItemKey = (data: any, value: string) => {
  if (!isEmpty(data)) {
    let key = null;
    for (const k in data) {
      if (data[k]?.toUpperCase() === value?.toUpperCase()) {
        key = k;
        break;
      }
    }
    return key;
  };
  return value;
};

/**
 * Provides a utility which determines the state of a CVSAssetType based on the Asset's
 * type, it's ignition status, and the speed at which the asset is moving.
 *
 * ```typescript
 *   import {getAssetState} from '@ion/core/src/entities/Asset';
 *
 *   console.log(getAssetState(AssetType.MACHINE, false, 0)); // Parked
 *   console.log(getAssetState(AssetType.VEHICLE, true, 60)); // Moving
 *   console.log(getCurrentState()); // ''
 *
 * ```
 *
 * @function getAssetState
 * @param    {AssetType} assetTypes The type of Asset; e.g. Vehicle, Machine, etc..
 * @param    {string|boolean} ignition If true, indicates that the ignition is on,
 *           otherwise, if false, indicates the ignition is off.
 * @param    {number} gpsSpeed (vbSpeed || gpsSpeed) The engine / speed of the Asset
 * @returns  {AssetState|string} An Asset AssetState; e.g. Active, Towed, etc. or
 *           an empty string if the assetType is null.
 *
 */
export const getAssetState = (
  {assetType, ignition, gpsSpeed, latitude, longitude}: BaseAsset
): (AssetState | EntityStatus) => {
  if (latitude && longitude) {
    const normaled    = titleCase(assetType) as AssetType;
    const isVehicle   = normaled ? !normaled.match(/Machine|Unpowered|Powered|Tool/gi) : true;
    const type        = isVehicle ? AssetType.Vehicle : normaled;
    const ignitionOn  = ignition === true || ignition === 'true';
    const ignitionOff = !ignitionOn;
    const isMoving    = !isNil(gpsSpeed) ? gpsSpeed > speedThreshold : false;
    const isNotMoving = !isNil(gpsSpeed) ? !isMoving : true;
    const conditions  = {
      Machine : {
        Active : ignitionOn,
        Towed  : ignitionOff && isMoving,
        Parked : ignitionOff && isNotMoving
      },
      Powered : {
        Moving : ignitionOn && isMoving,
        Idle   : ignitionOn && isNotMoving,
        Parked : ignitionOff && isNotMoving,
        Towed  : ignitionOff && isMoving
      },
      Unpowered : {
        Towed  : isMoving,
        Parked : isNotMoving
      },
      Vehicle : {
        Towed  : ignitionOff && isMoving,
        Parked : ignitionOff && isNotMoving,
        Moving : ignitionOn && isMoving,
        Idle   : ignitionOn && isNotMoving
      }
    };
    return invert(conditions[type]).true || '';
  } else {
    return AssetState.NOGPS;
  }
};

export const mapToolStatus = (tagReport) => {
  const online = !!Object.keys(tagReport).length;
  const value  = online ? ToolStatus.ONLINE : ToolStatus.DISCONNECTED;
  const status = online ? AssetState.ACTIVE : AssetState.TOWED;
  return {
    status,
    value
  };
};

/**
 * @function normalizeAsset
 *
 * Normalized an asset by deleting extrenous attributes and converting specific
 * values as needed.
 *
 * @param {Asset} asset The asset which is to be normalized.
 */
export const normalizeAsset = (asset: Asset) => {
  asset.odometerReference    = parseFloat(asset.odometerReference as string) as number;
  asset.engineHoursReference = parseInt(asset.engineHoursReference as string) as number;
  delete asset.hasExistingSchedule;
  delete asset.schedule;
  return asset;
};

/**
 * @deprecated
 * @findAssetEventIsHarsh
 *
 * compares a given event code against our events list and
 * finds if a given event is harsh or not.
 */
export function findAssetEventIsHarsh(eventCode: number): boolean {
  const harshCodeValues = [7, 8, 20, 21, 30, 31, 136, 137, 999];
  return harshCodeValues.includes(eventCode);
};

/**
 * @shouldDisplaySpeed
 *
 * compares a given event code against our events list and
 * finds if it is one that should display speed in the asset history
 * grid
 */
export function shouldDisplaySpeed(eventCode: number): boolean {
  const withSpeedValues = [6, 7, 8, 20, 137, 140];
  return withSpeedValues.includes(eventCode);
};

/**
 * @isIgnitionOff
 *
 * Returns boolean value of whether or not an event is ignition off
 */
export function isIgnitionOff(event: HVSRecord): boolean {
  return event?.eventCode === 3;
};

/**
 * Provides a utility which returns true if item exists in the list,
 * otherwise return false
 */
export const hasItem = (list: any[], property: string, value: number): boolean => {
  return Boolean(list?.find(item => item?.[property] === value));
};

/**
 * Provides a utility which returns an array of points, the selected asset is included
 *
 * ```typescript
 * import {addSelectedAsset} from '@ion/core/src/entities';
 *
 * const assets = addSelectedAsset(current?.points, points, selectedAsset, include);
 *
 * ```
 *
 * @method addSelectedAsset
 *  @param {any[]} clusters The array of points from the supercluster utility process
 *  @param {Array<CVSAssetType>} assets The array of assets from the query assets results
 *  @param {CVSAssetType} selectedAsset The selected asset object
 *  @param {boolean} include it's used to force to include the selected asset into the list
 */
export const addSelectedAsset = ({
  items,
  assets,
  selectedAsset,
  include = false
}: AddAsset): any[] => {
  if (selectedAsset) {
    const hasAssets = Boolean(assets?.length > 1);
    const isValid   = !hasItem(items, 'assetId', selectedAsset?.assetId);
    if (hasAssets && !include) {
      const isAvailable = hasItem(assets, 'id', selectedAsset.id);
      if (isValid && isAvailable) {
        items.push(selectedAsset);
      }
    } else if (!hasAssets && isValid && include) {
      items.push(selectedAsset);
    }
  }
  return items;
};

/**
 * Provides a utility which returns an array of assets without selected asset
 *
 * ```typescript
 * import {removeSelectedAsset} from '@ion/core/src/entities';
 *
 * const assets = removeSelectedAsset(points, selectedAsset);
 *
 * ```
 *
 * @method removeSelectedAsset
 *  @param {Array<CVSAssetType>} assets The array of assets from the query assets results
 *  @param {CVSAssetType} selectedAsset The selected asset object
 */
export const removeSelectedAsset = (
  assets: Array<CVSAssetType>,
  selectedAsset: CVSAssetType): Array<CVSAssetType> => {
  if (assets?.length > 1 && selectedAsset) {
    return assets.filter(({id}) => id !== selectedAsset?.id);
  }
  return assets;
};

/**
 * @function getLatestTime
 *
 * Provides a utility which returns the latest time from array of graphics
 * based on the field parameter provided
 *
 * @param {Array<__esri.Graphic>} graphics array of esri graphics
 * @param {string} field the field to use for the computation
 *
 * ```typescript
 *      import {getLatestTime} from '@ion/core/src/entities';
 *
 *      const latestTime = getLatestTime(graphics, 'dbWriteTime');
 *
 * ```
 */
export const getLatestTime = (graphics: Array<__esri.Graphic>, fieldName: string): number => {
  if (graphics?.length) {
    const times = graphics.map((i) => i[fieldName]);
    return Math.max(...times);
  }
  return 0;
};

/**
 * The createAssetSearchTerms utility iterates the list of imported entities,
 * and returns an array of search terms for each group which contains assets.
 *
 * @param   resolved Array of resolved entities which are to be imported.
 *          Entities are considered to be resolved if the group to which they are
 *          to be added exists within the current global group hierarchy.
 * @returns An array of asset searchTerm objects, whereby each object
 *          defines all assets as the asset search term for each group.
 */
export const createAssetSearchTerms = (resolved) => {
  const groupAssetNames = {};
  const assetNames = [];
  resolved?.forEach(({operator}) => {
    const {asset, account} = operator;
    if (asset) {
      const groupId = entityId(account);
      groupAssetNames[groupId] = groupAssetNames[groupId] || [];
      groupAssetNames[groupId]?.push(asset);
    }
  });
  Object.keys(groupAssetNames)?.forEach(groupId => {
    assetNames?.push({
      name : {
        value    : groupAssetNames[groupId]?.toString(),
        wildcard : false
      },
      account : {
        value : groupId
      }
    });
  });
  return assetNames;
};

/**
 * Provides a convenience function which validates the Asset Name for the
 * list of imported entities against the Assets retrieved, returning an object
 * which contains the valid and invalid entities.
 *
 * ```typescript
 *
 * import {validateAssetGroups} from '@ion/core/src/entities';
 * const {valid, invalid} = validateAssetGroups(resolved, assets);
 * ```
 *
 * @param   resolved entities containing asset which are to be validated.
 * @param   assets A mapping of group ids which contains the asset
 *          search results for each entity which defined a asset name.
 * @returns An object providing a valid property which contains the
 *          list of valid entities with assets, and an invalid property which
 *          contains the list of invalid entities with assets (i.e. either the
 *          asset could not be found, or the asset is not defined within
 *          the current group hierarchy).
 */
export const validateAssetGroups = (resolved, assets) => {
  const valid     = [];
  const invalid   = [];
  const validated = {};
  const hrefs = {};
  Object.keys(assets)?.forEach(groupId => {
    const results = assets[groupId];
    results?.forEach((asset) => {
      validated[asset.name] = true;
      hrefs[asset.name] = asset._self.href;
    });
  });
  resolved?.forEach(({operator}) => {
    const {asset} = operator;
    if (asset && !validated[asset]) {
      invalid.push({operator});
    } else {
      if (asset && hrefs[asset]) {
        operator.asset = {
          href : hrefs[asset],
          rel  : 'asset'
        };
      }
      valid.push({operator});
    }
  });
  return {valid, invalid};
};

/**
 * Custom sort comparator function for comparing 2 HVS Records
 *
 * @param dataA HVS Record to be compared
 * @param dataB HVS Record to be compared
 * @returns a number that a sort function can use to determine sorting
 */
export function statusSortComparator(dataA: HVSRecord, dataB: HVSRecord) {
  if (dataA && dataB) {
    const nodeAValue = getAssetState(dataA);
    const nodeBValue = getAssetState(dataB);
    if (nodeAValue === nodeBValue) return 0;
    return (nodeAValue > nodeBValue) ? 1 : -1;
  }
  return -1;
}

/**
 * Provides a convenience function which returns a formatted address
 * for the given asset based on the street, city, state, postalCode
 * asset attributes.
 *
 * @param value Asset input value
 * @param onVal On string value. Default is "ON"
 * @param offVal Off string value. Default is "OFF"
 */
export const getInputsOnOffFormat = (val, onVal = 'ON', offVal = 'OFF') => {
  if (!isNil(val)) {
    if (val.startsWith('{')) {
      try {
        const parsed = safeJSONParse(val) || {} as any;
        const {label, value, active} = parsed;
        if (!(isEmpty(label) || isEmpty(value) || label?.toLowerCase().startsWith('input'))) {
          return `${label} ${value}`;
        } else {
          return isTrue(active) ? onVal : offVal;
        }
      } catch (e) {
        return offVal;
      }
    }
    return val ? (isTrue(val) ? onVal : offVal) : '';
  }
  return offVal;
};

/**
*
* toFixedVoltage converts a raw millivolt reading to volts, fixed to the
* given number of decimal digits - shared so callers needing a different
* precision (e.g. a compact tile display) aren't left re-deriving the same
* /1000 conversion independently.
*
* @param battery raw millivolt value
* @param digits  decimal digits to fix to (default: 3, matching
*                `getBatteryVoltageFixed`'s existing output)
*
*/
export const toFixedVoltage = (battery: number, digits: number = 3): string => {
  return battery ? (battery / 1000).toFixed(digits) : '0';
};

/**
*
* getBatteryVoltageFixed method helps to return Battery Voltage formatted [14.100]
*
* @param battery asset battery value
*
*/
export const getBatteryVoltageFixed = (battery: number): string => {
  return toFixedVoltage(battery, 3);
};

/**
 * Defines a utlity method to return Battery value
 *
 * ```typescript
 *  import {getBattery} from '@ion/core/src/entities';
 *
 *  const battery = getBattery(item);
 *
 * ```
 */
export const getBattery = (item: BaseAsset) => {
  return item?.batteryLevelValue ?? item?.evBatteryPackRemainingCharge;
};
